import { useRef, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

export type RouterLocation = { pathname: string; search: string; hash: string };
type LocationListener = (location: RouterLocation) => void;

/**
 * Custom hook to retrieve the current router location object.
 * This hook only exposes the properties that we use in our application:
 * - pathname: The path of the current URL.
 * - search: The query string of the current URL.
 * - hash: The hash fragment of the current URL.
 * This hook should be used instead of `useLocation` as a router agnostic way
 * to access the location.
 *
 * It will allow us to change the router implementation in the future.
 */
export function useRouterLocation(): RouterLocation {
	const { pathname, search, hash } = useLocation();
	return useMemo(
		() => ({
			// matches react-router v5 behavior of decoding pathname
			pathname: decodeURI(pathname),
			search,
			hash
		}),
		[pathname, search, hash]
	);
}

/*
 * Custom hook to listen for location changes.
 * The returned function can be invoked to start listening for location changes.
 * The listeners return value can be invoked to stop listening for location changes.
 */
export const useLocationListener = () => {
	const location = useRouterLocation();
	const listenerRef = useRef<LocationListener | null>(null);

	useEffect(() => {
		if (listenerRef.current) {
			listenerRef.current(location);
		}
	}, [location]);

	return (callback: LocationListener) => {
		listenerRef.current = callback;

		// Return an unlisten function
		return () => {
			listenerRef.current = null;
		};
	};
};
